import { useRef, type ReactNode } from 'react';
import { REVEAL_TIMING, useInView } from '@/utils/animation';
import { cn } from '@/utils/cn';

type Direction = 'up' | 'down' | 'left' | 'right';

const TRANSFORMS: Record<Direction, string> = {
  up: `translateY(${REVEAL_TIMING.offsetPx}px)`,
  down: `translateY(-${REVEAL_TIMING.offsetPx}px)`,
  left: `translateX(-${REVEAL_TIMING.offsetPx}px)`,
  right: `translateX(${REVEAL_TIMING.offsetPx}px)`,
};

export interface RevealOnViewProps {
  children: ReactNode;
  /**
   * Direction the element starts FROM relative to its final position.
   * `up`    — starts 60px below, slides up.
   * `down`  — starts 60px above, slides down.
   * `left`  — starts 60px left, slides right.
   * `right` — starts 60px right, slides left.
   * Default `up`.
   */
  from?: Direction;
  /** IntersectionObserver threshold. Default 0 — fires the moment the
   *  element first enters the viewport. A higher threshold can fail to
   *  fire on elements taller than the viewport. */
  threshold?: number;
  /** Observer `rootMargin`. Default `'0px'`. */
  rootMargin?: string;
  /** Delay (ms) before the transition kicks off after the element
   *  intersects the viewport. Default 0. */
  delayMs?: number;
  /** Override the duration. Defaults to `REVEAL_TIMING.durationMs`. */
  durationMs?: number;
  /**
   * Fractional offset (0–1) for the opacity transition's start, expressed
   * as a portion of the slide duration. `0.3` means the fade begins 30%
   * of the way through the slide. The fade still ends at the same moment
   * as the slide — its duration is shortened to compensate. Default 0
   * (slide and fade start together).
   */
  fadeStart?: number;
  /**
   * When provided, bypasses the wrapper's own `IntersectionObserver` and
   * uses this value to drive the reveal. Lets a parent orchestrate
   * multiple reveals from a single observer (e.g. staggered rows).
   */
  inView?: boolean;
  /** className applied to the wrapper. */
  className?: string;
}

/**
 * Reveal-on-scroll wrapper — slides the children in from the chosen
 * `from` direction while fading their opacity from 0 → 1 in lockstep.
 * Pulls easing/duration/offset from the shared `REVEAL_TIMING` tokens
 * so every reveal across the site shares the exact same feel.
 *
 * The animation fires the first time the wrapper element intersects
 * the viewport (per `useInView`) and never replays.
 */
export function RevealOnView({
  children,
  from = 'up',
  threshold = 0,
  rootMargin = '0px',
  delayMs = 0,
  durationMs = REVEAL_TIMING.durationMs,
  fadeStart = 0,
  inView,
  className,
}: RevealOnViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const internalInView = useInView(ref, { threshold, rootMargin });
  const isVisible = inView ?? internalInView;

  const fadeOffsetMs = Math.round(durationMs * Math.min(Math.max(fadeStart, 0), 1));
  const opacityDelayMs = delayMs + fadeOffsetMs;
  const opacityDurationMs = Math.max(0, durationMs - fadeOffsetMs);

  return (
    <div
      ref={ref}
      className={cn(className)}
      style={{
        transform: isVisible ? 'translate(0, 0)' : TRANSFORMS[from],
        opacity: isVisible ? 1 : 0,
        transition: `transform ${durationMs}ms ${REVEAL_TIMING.ease} ${delayMs}ms, opacity ${opacityDurationMs}ms ${REVEAL_TIMING.ease} ${opacityDelayMs}ms`,
      }}
    >
      {children}
    </div>
  );
}
